import { APIDelete, APIGet, APIPost, APIPut, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";


interface IPutStep1EmailCampaign {
  id: number;
  name: string;
  description: string;
}
interface IPutStep2EmailCampaign {
  id: number;
  audience_ids: number[];

  from_name: string;
  from_address: string;

  subject: string;
  text_preview: string;
}

interface IPutPublishEmailCampaign {
  id: number;
}

interface IGetAudienceCustomerExport {
  id: number;
}
interface IGetAudienceCustomer {
  id: number;
  campaign_id?: number;
  page: number;
  search?: string;
  filters?: IAudienceFilter[];
  has_filters?: boolean;
  imported_customer_ids?: number[];
  removed_customer_ids?: number[];
}

interface IPostAudience {
  title: string;
  type: IAudience['type'];
  filters: IAudienceFilter[];
}
interface IPutAudience {
  id: number;
  title: string;
  type: IAudience['type'];
  imported_customer_ids?: number[];
  removed_customer_ids?: number[];
  filters: IAudienceFilter[];
}

export interface IEmailCampaignDesignStatus {
  has_draft: boolean;
  draft_revision: number | null;
  active_published_design_id: number | null;
  active_published_version: number | null;
  active_published_source_draft_revision: number | null;
  has_unpublished_changes: boolean;
}

export interface IEmailCampaign {
  id: number;
  name: string; // TODO: Decide on name or title
  type: string;
  description: string;
  status: 'published' | 'pending' | 'scheduled' | 'draft';
  subject?: string;
  text_preview?: string;
  content: string;
  audiences?: IAudience[];
  audiences_count?: number;
  from_name?: string;
  from_address?: string;
  scheduled_type: 'now' | 'later' | 'recurring';
  scheduled_recurring_type?: 'recurring_weekly' | 'recurring_monthly' | 'recurring_biannually' | 'recurring_annually';
  scheduled_at?: string;
  published_at?: string;
  design?: IEmailCampaignDesignStatus;
  created_at?: string;
  updated_at?: string;
}

export interface IAudience {
  id: number;
  type: 'static' | 'smart' | null;
  title: string;
  included_customer_count: number;
  total_customer_count: number;
  filters: IAudienceFilter[];
  created_at: string;
  updated_at: string;
}
export interface IAudienceFilter {
  field: string;
  operator: string;
  value: (string | number)[];
}
export interface IAudienceCustomer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  status: 'active' | 'excluded' | 'unsubscribed';
  old_status?: IAudienceCustomer['status'];
  excluded: boolean;
  preference_marketing_email: boolean;
}

export interface IPaginatedResponse<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: {
    url: string | null;
    label: string;
    active: boolean;
  }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

interface IGetAudienceCustomersResponse {
  customers: IPaginatedResponse<IAudienceCustomer>;
  excluded_count: number;
  included_count: number;
  customers_from_filters_count: number;
}

export async function GetEmailCampaigns(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IEmailCampaign[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns"), params, useGlobalLoader);
}

export async function PostEmailCampaign(params: unknown, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaign }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns"), params, useGlobalLoader);
}

export async function PutStep1EmailCampaign(params: IPutStep1EmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign & { message: string }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/step1"), params, useGlobalLoader);
}
export async function PutStep2EmailCampaign(params: IPutStep2EmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign & { message: string }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/step2"), params, useGlobalLoader);
}

export async function PutPublishEmailCampaign(params: IPutPublishEmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign & { message: string }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/publish"), params, useGlobalLoader);
}

export async function PutScheduleEmailCampaign(
  params: {
    id: number;
    scheduled_type: IEmailCampaign["scheduled_type"];
    scheduled_recurring_type?: IEmailCampaign["scheduled_recurring_type"] | null;
    scheduled_at: string | null;
  },
  useGlobalLoader: boolean = false,
) {
  return await APIPut<IEmailCampaign & { message: string }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/schedule"),
    params,
    useGlobalLoader,
  );
}

export async function GetAudience(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}
export async function GetAudiences(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IAudience[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audiences"), params, useGlobalLoader);
}
export async function GetAudienceCustomers(params: IGetAudienceCustomer, useGlobalLoader: boolean = false, cancelToken?: CancelToken) {
  return await APIGet<IGetAudienceCustomersResponse>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers"), params, useGlobalLoader, cancelToken);
}
export async function PostAudienceCustomers(params: FormData, useGlobalLoader: boolean = false) {
  return await APIPost<IAudienceCustomer[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers"), params, useGlobalLoader);
}
export async function GetAudienceCustomersExport(params: IGetAudienceCustomerExport, useGlobalLoader: boolean = false) {
  return await APIGet<string>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers/export"), params, useGlobalLoader);
}
export async function PostAudience(params: IPostAudience, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IAudience }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}
export async function PutAudience(params: IPutAudience, useGlobalLoader: boolean = false) {
  return await APIPut<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}
export async function DeleteAudience(params: { id: number }, useGlobalLoader: boolean = false) {
  return await APIDelete<void>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}

export async function PostCampaignDuplicate(params: { id: number; }, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaign }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/duplicate"), params, useGlobalLoader);
}

export async function PostCampaignUnpublish(params: { id: number }, useGlobalLoader: boolean = false) {
  return await APIPost<{ data: IEmailCampaign }>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/unpublish"), params, useGlobalLoader);
}

export async function DeleteCampaign(params: { id: number; }, useGlobalLoader: boolean = false) {
  return await APIPost<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/delete"), params, useGlobalLoader);
}

export async function PostToggleExclude(params: { id: number; customer_id: number, excluded: boolean }, useGlobalLoader: boolean = false) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customer/toggle"), params, useGlobalLoader);
}

export async function PostAudienceSubscribeMarketing(
  params: { id: number },
  useGlobalLoader: boolean = false,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/subscribe-marketing"), params, useGlobalLoader);
}

export async function PostAudienceUnsubscribeMarketing(
  params: { id: number },
  useGlobalLoader: boolean = false,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/unsubscribe-marketing"), params, useGlobalLoader);
}

export interface IEmailMarketingSettings {
  allow_opt_ins: boolean;
  booking_page_checkbox: boolean;
  sign_up_button: boolean;
}
export async function PutEmailMarketingSettings(params: IEmailMarketingSettings, useGlobalLoader: boolean = false) {
  return await APIPut<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-marketing/settings"), params, useGlobalLoader);
}

export type TEmailCampaignAnalyticsDateRange = "last_7_days" | "last_30_days" | "last_90_days" | "all_time";

export interface IEmailCampaignAnalyticsMetric {
  count: number;
  rate: number;
}

export interface IEmailCampaignFunnel {
  sent: number;
  delivered: IEmailCampaignAnalyticsMetric;
  opened: IEmailCampaignAnalyticsMetric;
  clicked: IEmailCampaignAnalyticsMetric & { total_clicks: number };
}

export interface IEmailCampaignTopLink {
  url: string;
  count: number;
  label?: string | null;
}

export interface IEmailCampaignAnalyticsBookingCategory {
  booking_category: string;
  booking_category_id: number;
  sent: number;
  delivered: IEmailCampaignAnalyticsMetric;
  opened: IEmailCampaignAnalyticsMetric;
  clicked: IEmailCampaignAnalyticsMetric;
}

export interface IEmailCampaignAnalyticsMemberType {
  customer_type_id: number | null;
  title: string;
  booking_category_id: number | null;
  booking_category: string;
  sent: number;
  delivered: IEmailCampaignAnalyticsMetric;
  opened: IEmailCampaignAnalyticsMetric;
  clicked: IEmailCampaignAnalyticsMetric;
}

export interface IEmailCampaignAnalytics {
  campaign_id: number;
  send_batch_id: string | null;
  summary: {
    delivered: IEmailCampaignAnalyticsMetric;
    opened: IEmailCampaignAnalyticsMetric;
    clicked: IEmailCampaignAnalyticsMetric;
    bounced: IEmailCampaignAnalyticsMetric;
  };
  funnel: IEmailCampaignFunnel;
  top_links: IEmailCampaignTopLink[];
  booking_categories: IEmailCampaignAnalyticsBookingCategory[];
  member_types: IEmailCampaignAnalyticsMemberType[];
}

interface IGetEmailCampaignAnalytics {
  id: number;
  send_batch_id?: string;
  from?: string;
  to?: string;
}

const EMPTY_ANALYTICS_METRIC: IEmailCampaignAnalyticsMetric = { count: 0, rate: 0 };

export const EMPTY_EMAIL_CAMPAIGN_ANALYTICS: IEmailCampaignAnalytics = {
  campaign_id: 0,
  send_batch_id: null,
  summary: {
    delivered: { ...EMPTY_ANALYTICS_METRIC },
    opened: { ...EMPTY_ANALYTICS_METRIC },
    clicked: { ...EMPTY_ANALYTICS_METRIC },
    bounced: { ...EMPTY_ANALYTICS_METRIC },
  },
  funnel: {
    sent: 0,
    delivered: { ...EMPTY_ANALYTICS_METRIC },
    opened: { ...EMPTY_ANALYTICS_METRIC },
    clicked: { ...EMPTY_ANALYTICS_METRIC, total_clicks: 0 },
  },
  top_links: [],
  booking_categories: [],
  member_types: [],
};

export async function GetEmailCampaignAnalytics(
  params: IGetEmailCampaignAnalytics,
  useGlobalLoader: boolean = false,
) {
  return await APIGet<IEmailCampaignAnalytics>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/analytics"),
    params,
    useGlobalLoader,
  );
}

export type TEmailCampaignAnalyticsMetric =
  | 'delivered'
  | 'opened'
  | 'bounced'
  | 'funnel'
  | 'top_links'
  | 'member_types'
  ;

export interface IEmailCampaignAnalyticsRecipient {
  id: number;
  customer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string;
  segment_type: string | null;
  member_type: string | null;
  customer_type_id: number | null;
  booking_category_id: number | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  delivery_status: "Delivered" | "Not delivered";
  open_status: "Opened" | "Not opened";
  click_status: "Clicked" | "Not clicked";
  bounce_type: string | null;
  bounce_reason: string | null;
  link_clicked: string | null;
  action: string;
  action_label: string;
}

export interface IGetEmailCampaignAnalyticsRecipientsResponse {
  recipients: IPaginatedResponse<IEmailCampaignAnalyticsRecipient>;
}

export interface IGetEmailCampaignAnalyticsRecipients {
  id: number;
  metric: TEmailCampaignAnalyticsMetric;
  page?: number;
  search?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  url?: string;
  send_batch_id?: string;
  from?: string;
  to?: string;
  per_page?: number;
  customer_type_id?: number;
  booking_category_id?: number;
}

export async function GetEmailCampaignAnalyticsRecipients(
  params: IGetEmailCampaignAnalyticsRecipients,
  useGlobalLoader: boolean = false,
) {
  return await APIGet<IGetEmailCampaignAnalyticsRecipientsResponse>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/analytics/recipients"),
    params,
    useGlobalLoader,
  );
}
export async function GetEmailCampaignAnalyticsRecipientsExport(
  params: IGetEmailCampaignAnalyticsRecipients,
  useGlobalLoader: boolean = false,
) {
  return await APIGet<string>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/analytics/recipients/export"),
    params,
    useGlobalLoader,
  );
}

interface IPostEmailCampaignAnalyticsRecipientsResend {
  id: number;
  email_send_id: number;
}

export async function PostEmailCampaignAnalyticsRecipientsResend(
  params: IPostEmailCampaignAnalyticsRecipientsResend,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ id: number; tracking_token: string }>(
    APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/analytics/recipients/resend"),
    params,
    useGlobalLoader,
  );
}

